import { db } from '@/firebase/init'
import { useTurnaroundTimeStore } from '@/stores/turnaroundTimeStore'
import { useUserStore } from '@/stores/userStore'
import { Analysis } from '@team-milva/lab-models/resources'
import type { IAnalysis } from '@team-milva/lab-models/resources'
import { CustomerAccountPrice } from '@team-milva/lab-models/customerAccount'
import type { ICustomerAccountPrice } from '@team-milva/lab-models/customerAccount'
import { InsuranceCustomerAccountPrice } from '@team-milva/lab-models/insuranceCustomerAccount'
import type { IInsuranceCustomerAccountPrice } from '@team-milva/lab-models/insuranceCustomerAccount'
import { customerOwnerContext } from '@/globals/typescript/utils/api/customerOwnerApi'
import { PricingSegment } from '@team-milva/lab-models/pricingSegment'
import type { IPricingSegment } from '@team-milva/lab-models/pricingSegment'
import { mergePrices } from '@team-milva/lab-utils/prices'
import type { IMergedPriceAnalysis } from '@team-milva/lab-utils/prices'
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore'
import { defineStore } from 'pinia'

/**
 * The signed-in owner's own price document (MIL-402): a business customer's
 * `CustomerAccountPrices/{id}` or an insurance customer's
 * `InsuranceCustomerAccountPrices/{id}`. Same shape — both carry the
 * pricingSegmentID, overrides and TATs mergePrices reads.
 */
export type TOwnerAccountPrice = CustomerAccountPrice | InsuranceCustomerAccountPrice

// Analyses are read live (labs actively manage their catalogue), but the
// customer's PricingSegment is a dependent lookup — its ID only becomes known
// once the CustomerAccountPrice doc loads, and can change over time. The
// segment listener is (re-)established inside the price listener's callback
// rather than fetched once, so a segment reassignment is reflected live too.
export const useAnalysisStore = defineStore('analysis', {
  state: () => ({
    allAnalyses: [] as Analysis[],
    unsubscribeAllAnalyses: null as (() => void) | null,

    customerAccountPrice: null as TOwnerAccountPrice | null,
    unsubscribeCustomerAccountPrice: null as (() => void) | null,

    pricingSegment: null as PricingSegment | null,
    unsubscribePricingSegment: null as (() => void) | null,
    subscribedPricingSegmentID: null as string | null,
  }),

  actions: {
    getAnalyses(): Promise<Analysis[]> {
      if (this.allAnalysesLoaded) {
        return Promise.resolve(this.allAnalyses)
      }

      const userStore = useUserStore()

      return new Promise((resolve) => {
        if (!userStore.currentUser) {
          return resolve([])
        }

        let resolved = false
        const q = query(
          collection(db, 'Apps', '-lab-management-', 'Analyses'),
          where('accountID', '==', userStore.currentUser.labAccountID)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          const analyses: Analysis[] = []
          querySnapshot.forEach((docSnap) => {
            analyses.push(new Analysis(docSnap.data() as IAnalysis))
          })
          this.allAnalyses = analyses
          this.unsubscribeAllAnalyses = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(analyses)
          }
        })
      })
    },

    // Loads the owner's price document from the collection its `customer.type`
    // names: `CustomerAccountPrices` for a business user,
    // `InsuranceCustomerAccountPrices` for an insurance user (MIL-402, Phase 6 —
    // both what can be ordered and what it costs, matching lab-cloud's
    // place-order pricing). A private user has no price document.
    getCustomerAccountPrice(): Promise<TOwnerAccountPrice | null> {
      if (this.customerAccountPriceLoaded) {
        return Promise.resolve(this.customerAccountPrice)
      }

      const owner = customerOwnerContext(useUserStore().currentUser)

      return new Promise((resolve) => {
        if (!owner) {
          return resolve(null)
        }

        const isInsurance = owner.customer.type === 'insurance'
        let resolved = false
        const unsubscribe = onSnapshot(
          doc(
            db,
            'Apps',
            '-lab-management-',
            isInsurance ? 'InsuranceCustomerAccountPrices' : 'CustomerAccountPrices',
            owner.customer.id
          ),
          (docSnap) => {
            if (!docSnap.exists()) {
              this.customerAccountPrice = null
            } else if (isInsurance) {
              this.customerAccountPrice = new InsuranceCustomerAccountPrice(
                docSnap.data() as IInsuranceCustomerAccountPrice
              )
            } else {
              this.customerAccountPrice = new CustomerAccountPrice(docSnap.data() as ICustomerAccountPrice)
            }
            this.unsubscribeCustomerAccountPrice = unsubscribe

            this.syncPricingSegmentSubscription()

            if (!resolved) {
              resolved = true
              resolve(this.customerAccountPrice)
            }
          }
        )
      })
    },

    // Tears down and re-establishes the PricingSegment listener whenever the
    // customer's assigned pricingSegmentID changes (including becoming null).
    syncPricingSegmentSubscription(): void {
      const pricingSegmentID = this.customerAccountPrice?.pricingSegmentID ?? null

      if (pricingSegmentID === this.subscribedPricingSegmentID) {
        return
      }

      this.unsubscribePricingSegment?.()
      this.unsubscribePricingSegment = null
      this.pricingSegment = null
      this.subscribedPricingSegmentID = pricingSegmentID

      if (!pricingSegmentID) {
        return
      }

      this.unsubscribePricingSegment = onSnapshot(
        doc(db, 'Apps', '-lab-management-', 'PricingSegments', pricingSegmentID),
        (docSnap) => {
          this.pricingSegment = docSnap.exists()
            ? new PricingSegment(docSnap.data() as IPricingSegment)
            : null
        }
      )
    },

    resetStore(): void {
      this.allAnalyses = []
      this.unsubscribeAllAnalyses?.()
      this.unsubscribeAllAnalyses = null

      this.customerAccountPrice = null
      this.unsubscribeCustomerAccountPrice?.()
      this.unsubscribeCustomerAccountPrice = null

      this.pricingSegment = null
      this.unsubscribePricingSegment?.()
      this.unsubscribePricingSegment = null
      this.subscribedPricingSegmentID = null
    },
  },

  getters: {
    allAnalysesLoaded: (state) => !!state.unsubscribeAllAnalyses,
    customerAccountPriceLoaded: (state) => !!state.unsubscribeCustomerAccountPrice,

    // Merged eligibility/pricing per analysis that has EITHER a segment price
    // or a customer override — mergePrices only returns entries for those,
    // even though `allAnalyses` is passed in (it's used solely to compute TAT
    // compatibility, not to include zero-price analyses in the result). An
    // analysis with no pricing configured anywhere will be present in
    // `allAnalyses` but absent here. Consumers that need to show every
    // catalogue analysis (including fully unconfigured ones) must left-join
    // `allAnalyses` against this list by `analysisID` and treat a miss as
    // not-orderable.
    mergedAnalyses(): IMergedPriceAnalysis[] {
      return this.mergedPriceResult.all.analyses
    },

    // Analyses that actually have a price and an active TAT (or no TATs at
    // all) — i.e. genuinely orderable right now. Per MIL-192/MIL-199, an
    // analysis without a configured PricingSegment/CustomerAccountPrice entry
    // (or one that's disabled, or has no active TAT) must not be offered as a
    // sample type at all, rather than shown disabled — this is the set to use
    // for that selection UI.
    orderableMergedAnalyses(): IMergedPriceAnalysis[] {
      return this.mergedPriceResult.active.analyses
    },

    mergedPriceResult(): ReturnType<typeof mergePrices> {
      const turnaroundTimeStore = useTurnaroundTimeStore()

      return mergePrices({
        segmentAnalyses: this.pricingSegment?.analyses ?? [],
        customerAnalyses: this.customerAccountPrice?.analyses ?? [],
        segmentTats: this.pricingSegment?.tats ?? [],
        customerTats: this.customerAccountPrice?.tats ?? [],
        allAnalyses: this.allAnalyses,
        allTats: turnaroundTimeStore.allTATs,
      })
    },
  },
})
